import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Plus } from 'lucide-react';
import { motion } from 'framer-motion';
import Magnetic from '../fx/Magnetic';
import { EASE_EXPO } from '../fx/ease';

// 左レール(w-14)を xl でクリアする共通コンテナ余白
export const PADX = 'px-6 md:px-10 lg:px-14 xl:pl-24 xl:pr-14';

export function Crosshair({ className = '', size = 24 }) {
  return <Plus className={className} size={size} strokeWidth={1} aria-hidden="true" />;
}

/** "02 — PRODUCTS" 形式のセクションラベル。罫線がドローインする。dark: 黒背景セクション用 */
export function SectionLabel({ no, children, dark = false, className = '' }) {
  return (
    <div
      className={`flex items-center gap-3 text-[11px] font-semibold uppercase tracking-label md:text-xs ${
        dark ? 'text-gray-500' : 'text-gray-600'
      } ${className}`}
    >
      {no && <span className="text-live tnum">{no}</span>}
      <motion.span
        className={`h-px w-8 origin-left ${dark ? 'bg-paper/40' : 'bg-ink/40'}`}
        initial={{ scaleX: 0 }}
        whileInView={{ scaleX: 1 }}
        // 左右は縮めない（幅0から伸ばす線が画面の左端付近にあると、入ったと判定されないため）
        viewport={{ once: true, margin: '-10% 0px' }}
        transition={{ duration: 0.8, ease: EASE_EXPO, delay: 0.15 }}
        aria-hidden="true"
      />
      <span>{children}</span>
    </div>
  );
}

const BTN_BASE =
  'group relative inline-flex items-center justify-center gap-3 overflow-hidden px-8 py-4 text-[14px] font-semibold min-h-[48px] transition-colors duration-300';
const BTN_VARIANTS = {
  live: 'bg-live text-[#F6F4EF] hover:opacity-90',
  ink: 'bg-ink text-paper',
  outline: 'border border-ink text-ink hover:bg-ink hover:text-paper',
  'outline-paper': 'border border-paper/70 text-paper hover:bg-paper hover:text-ink',
};

/**
 * マグネティックCTA。to(内部) / href(外部) / onClick(ボタン) を受ける。
 */
export function MagneticCTA({
  to,
  href,
  onClick,
  variant = 'live',
  icon = 'right',
  className = '',
  cursorLabel,
  htmlType = 'button',
  disabled = false,
  children,
}) {
  const Icon = icon === 'up' ? ArrowUpRight : ArrowRight;
  const move =
    icon === 'up'
      ? 'group-hover:translate-x-1 group-hover:-translate-y-1'
      : 'group-hover:translate-x-1';
  const cls = `${BTN_BASE} ${BTN_VARIANTS[variant]} ${className}`;
  const inner = (
    <>
      <span className="relative z-10">{children}</span>
      <Icon
        size={16}
        className={`relative z-10 transition-transform duration-300 ${move}`}
        aria-hidden="true"
      />
    </>
  );
  const props = cursorLabel ? { 'data-cursor': cursorLabel } : {};

  let el;
  if (href) {
    el = (
      <a href={href} target="_blank" rel="noopener noreferrer" className={cls} {...props}>
        {inner}
      </a>
    );
  } else if (to) {
    el = (
      <Link to={to} className={cls} onClick={onClick} {...props}>
        {inner}
      </Link>
    );
  } else {
    el = (
      <button
        type={htmlType}
        onClick={onClick}
        disabled={disabled}
        className={`${cls} disabled:opacity-50`}
        {...props}
      >
        {inner}
      </button>
    );
  }
  return <Magnetic strength={0.25}>{el}</Magnetic>;
}

export function TextLink({ to, href, onClick, icon = 'right', className = '', cursorLabel, children }) {
  const Icon = icon === 'up' ? ArrowUpRight : ArrowRight;
  const move =
    icon === 'up'
      ? 'group-hover:translate-x-1 group-hover:-translate-y-1'
      : 'group-hover:translate-x-1';
  const cls = `group inline-flex items-center gap-2 text-[13px] font-semibold uppercase tracking-label text-ink u-line ${className}`;
  const cursorProps = cursorLabel ? { 'data-cursor': cursorLabel } : {};
  const inner = (
    <>
      <span>{children}</span>
      <Icon size={15} className={`transition-transform duration-300 ${move}`} aria-hidden="true" />
    </>
  );
  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={cls} {...cursorProps}>
        {inner}
      </a>
    );
  }
  if (to) {
    return (
      <Link to={to} className={cls} onClick={onClick} {...cursorProps}>
        {inner}
      </Link>
    );
  }
  return (
    <button type="button" onClick={onClick} className={cls} {...cursorProps}>
      {inner}
    </button>
  );
}
